import { useMemo } from 'react';
import {
  OperationalModelOptions,
  OperationalStatusOptions,
} from '@components/EntityDrawers/constants/common';
import {
  ReadableReportInclusion,
  ReportInclusion,
} from '@constants/canvas/layers';
import { InsightKeys } from '@constants/entities/insights';
import { RefType } from '@constants/entities/project';
import { useProjectFilters } from '@context/ProjectFilters';
import useProjectData from '@hooks/useProjectData';
import useProjectId from '@hooks/useProjectId';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { IDomain } from '@store/services/projects/types';
import { useGetQnousRefsQuery } from '@store/services/qnous-ref';
import { IQnousRef } from '@store/services/qnous-ref/types';
import { CIASettingsCriteriaType } from '@store/services/questionnaire/types';
import { ModelFilterCategory } from '@store/zustand/model-filters';
import { buildCisCatalogue, CisCatalogue } from '@utils/cisCatalogue';
import {
  buildScopeTree,
  SCOPE_FILTER_UNASSIGNED,
  SCOPE_UNASSIGNED_LABEL,
  ScopeTree,
} from '@utils/domains';
import type {
  CiaFilterCategory,
  FlatModelFilterCategory,
  ModelFilterVocabulary,
} from '@utils/modelFilterCounts';
import {
  CATEGORY_LABELS,
  CategoryCriteria,
  CategoryOption,
  ciaCategoryTitle,
} from '@views/Project/components/ModelFiltersPopover/categories';

/**
 * What the popover needs to render and count every category: the value list
 * of each flat category, the criteria of each CIA category, the CIS-18
 * catalogue, the Scope tree the Scope panel lists, the name each row goes
 * by, the labels the root summary reads, which rows are shown at all, and
 * the same values in the shape the counts helper takes. A CIA category's
 * title is its single criteria's name or the default name, resolved here
 * once so the root row, the chip and the +N row cannot disagree.
 */
export type CategoryOptions = {
  options: Partial<Record<FlatModelFilterCategory, CategoryOption[]>>;
  criteria: Partial<Record<CiaFilterCategory, CategoryCriteria[]>>;
  cisCatalogue: CisCatalogue;
  scope: ScopeTree;
  titles: Record<ModelFilterCategory, string>;
  labels: Partial<Record<ModelFilterCategory, Record<string, string>>>;
  availability: Partial<Record<ModelFilterCategory, boolean>>;
  vocabulary: ModelFilterVocabulary;
};

const NO_DOMAINS: IDomain[] = [];
const NO_PARTNERS: string[] = [];
const NO_REFS: IQnousRef[] = [];

const REPORT_INCLUSION_OPTIONS: CategoryOption[] = [
  ReportInclusion.Included,
  ReportInclusion.NotIncluded,
].map((value) => ({ value, label: ReadableReportInclusion[value] }));

const toOption = ({
  value,
  label,
}: {
  value: string | number;
  label: string;
}): CategoryOption => ({ value: String(value), label });

const identityOptions = (values: string[]): CategoryOption[] =>
  values.map((value) => ({ value, label: value }));

/**
 * The Scope values in the order the panel lists them: Enterprise wide and
 * Unassigned, then each Domain followed by its Sub-domains.
 */
const scopeOptions = ({ wide, domains }: ScopeTree): CategoryOption[] => [
  ...(wide ? [{ value: wide.id, label: wide.label }] : []),
  { value: SCOPE_FILTER_UNASSIGNED, label: SCOPE_UNASSIGNED_LABEL },
  ...domains.flatMap((item) =>
    [item, ...item.subDomains].map(({ id, label }) => ({ value: id, label })),
  ),
];

const toCriteria = (
  criterias: CIASettingsCriteriaType[] | undefined,
): CategoryCriteria[] =>
  (criterias ?? []).map(({ id, name, answers }) => ({
    id,
    name,
    options: identityOptions(answers),
  }));

const valuesOf = (options: CategoryOption[]) =>
  options.map(({ value }) => value);

const labelsOf = (options: CategoryOption[]) =>
  Object.fromEntries(options.map(({ value, label }) => [value, label]));

const answersByCriteria = (criteria: CategoryCriteria[]) =>
  Object.fromEntries(
    criteria.map(({ id, options }) => [id, valuesOf(options)]),
  );

/**
 * The value vocabulary, display labels, criteria, catalogue and
 * availability of every category row.
 *
 * The client owns the vocabulary: a value no entity carries still gets a row,
 * reading 0 once counted. Validation and Review status come from the filters
 * context, the one place those two lists are defined; Operational status and
 * Operating model are the entity-drawer enums; partners are the project's;
 * Scope reads the same domains query the retiring Scope control read, so it
 * follows domain edits through the `ProjectDomains` tag; the three CIA
 * categories are the questionnaire's criteria; Report inclusion is its two
 * fixed values; and CIS-18 reference is the reference catalogue the drawer's
 * CIS-18 field reads, subscribed only while the project's reference model
 * includes CIS-18, so a project without it fetches nothing. Availability is
 * the project having a partner for Outsourcing partner; Scope is always
 * offered, since Enterprise wide and Unassigned exist with or without a
 * configured Domain; for CIA the gate the retiring `CiaFilters` applied — a
 * questionnaire, the CIA insight in the subscription — plus at least one
 * criteria to list; for Report inclusion the CIA insight alone, since every
 * entity carries the report flag whether or not a questionnaire exists; and
 * for CIS-18 reference the reference-model gate plus at least one visible
 * catalogue entry, so no row opens on an empty panel.
 */
export const useCategoryOptions = (): CategoryOptions => {
  const { validationStatusOptions, reviewStatusOptions } = useProjectFilters();
  const [
    { partners = NO_PARTNERS, questionnaire, subscription, settingsData },
  ] = useProjectData();
  const projectId = useProjectId();
  const { data: domains = NO_DOMAINS } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );
  const isCisOffered = Boolean(
    settingsData?.reference_model?.includes(RefType.CIS),
  );
  const { data: refs } = useGetQnousRefsQuery(
    isCisOffered ? undefined : skipToken,
  );
  const cisRefs = refs?.cis ?? NO_REFS;

  return useMemo(() => {
    const scope = buildScopeTree(domains);
    const options = {
      domains: scopeOptions(scope),
      validationStatus: validationStatusOptions.map(toOption),
      reviewStatus: reviewStatusOptions.map(toOption),
      operationalStatuses: OperationalStatusOptions.map(toOption),
      operatingModelStatuses: OperationalModelOptions.map(toOption),
      partners: identityOptions(partners),
      reportInclusion: REPORT_INCLUSION_OPTIONS,
    };

    const hasSbdInsight = Boolean(
      subscription?.insights.includes(InsightKeys.CIA),
    );
    const hasCia = Boolean(questionnaire) && hasSbdInsight;
    const ciaCriteria = hasCia ? questionnaire?.cia_criteria : undefined;

    const criteria = {
      assurance_level: toCriteria(ciaCriteria?.assurance_level.criterias),
      coverage: toCriteria(ciaCriteria?.coverage.criterias),
      applicability: toCriteria(ciaCriteria?.applicability.criterias),
    };

    const titles: CategoryOptions['titles'] = {
      ...CATEGORY_LABELS,
      assurance_level: ciaCategoryTitle(
        'assurance_level',
        criteria.assurance_level,
      ),
      coverage: ciaCategoryTitle('coverage', criteria.coverage),
      applicability: ciaCategoryTitle('applicability', criteria.applicability),
    };

    const cisCatalogue = buildCisCatalogue(cisRefs);

    const labels: CategoryOptions['labels'] = {
      domains: labelsOf(options.domains),
      validationStatus: labelsOf(options.validationStatus),
      reviewStatus: labelsOf(options.reviewStatus),
      operationalStatuses: labelsOf(options.operationalStatuses),
      operatingModelStatuses: labelsOf(options.operatingModelStatuses),
      partners: labelsOf(options.partners),
      reportInclusion: labelsOf(options.reportInclusion),
      cis: Object.fromEntries(
        cisCatalogue.references.map(({ id, label }) => [id, label]),
      ),
    };

    const availability: CategoryOptions['availability'] = {
      domains: true,
      partners: partners.length > 0,
      assurance_level: criteria.assurance_level.length > 0,
      coverage: criteria.coverage.length > 0,
      applicability: criteria.applicability.length > 0,
      reportInclusion: hasSbdInsight,
      cis: isCisOffered && cisCatalogue.references.length > 0,
    };

    const vocabulary: ModelFilterVocabulary = {
      domains: valuesOf(options.domains),
      validationStatus: valuesOf(options.validationStatus),
      reviewStatus: valuesOf(options.reviewStatus),
      operationalStatuses: valuesOf(options.operationalStatuses),
      operatingModelStatuses: valuesOf(options.operatingModelStatuses),
      partners: valuesOf(options.partners),
      reportInclusion: valuesOf(options.reportInclusion),
      cis: cisCatalogue.references.map(({ id }) => id),
      assurance_level: answersByCriteria(criteria.assurance_level),
      coverage: answersByCriteria(criteria.coverage),
      applicability: answersByCriteria(criteria.applicability),
    };

    return {
      options,
      criteria,
      cisCatalogue,
      scope,
      titles,
      labels,
      availability,
      vocabulary,
    };
  }, [
    domains,
    validationStatusOptions,
    reviewStatusOptions,
    partners,
    questionnaire,
    subscription,
    isCisOffered,
    cisRefs,
  ]);
};
